import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { LOAD_MORE_PAGE_SIZE, VALIDATION_POLL_INTERVAL_MS } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, Toolbar, TooltipPortal } from '~components';
import { SearchBar, SearchProps } from '~components/form-fields/search';
import Icon, { faFileInvoiceDollar, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import { useCanAdministerClaims } from '~support-at-home/hooks/useCanAdministerClaims';
import columnsFactory from '~support-at-home/forms/claims/claimsList';
import { parseSahApiError } from '~support-at-home/lib/sahApiError';
import { summariseUnmatchedItems } from '~support-at-home/lib/sweepErrors';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getStatuses } from '~appointments/actions/statuses';
import { setTitle } from '~main/actions/login';
import {
  useGetClaimableClaimsQuery,
  useGetClaimsQuery,
  useRefreshClaimStatusesMutation,
  useValidateClaimMutation,
} from '~support-at-home/actions/claims';

import ClaimsWizard from './claims-wizard';
import SubmitClaimsModal from './SubmitClaimsModal';

// Longer than the 3s default: this one names identifiers the provider has to read and act on, and it
// arrives unprompted from tab activation rather than from a button they just pressed.
const UNMATCHED_TOAST_DURATION_MS = 10000;

const searchFields: SearchProps = {
  start_date: {
    defaultShow: true,
    type: 'date-range',
    field: 'start_date',
    caption: 'Start Date',
  },
};

const Claims: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('claims-search', { limit: LOAD_MORE_PAGE_SIZE });
  const [claimsWizardOpen, setClaimsWizardOpen] = useState<boolean>(false);
  const [submitClaimsOpen, setSubmitClaimsOpen] = useState<boolean>(false);

  const handleSearchSubmit = (values: Record<string, any>) => setSearchParams({ ...values, limit: LOAD_MORE_PAGE_SIZE });

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE;
    setSearchParams({ ...searchParams, limit: currentLimit + LOAD_MORE_PAGE_SIZE });
  };

  // RTK Hooks
  const { data, isFetching: isLoading, refetch: refetchClaims } = useGetClaimsQuery(searchParams);
  const [validateClaim] = useValidateClaimMutation();
  const { showToast } = useToast();

  // Drives the Submit Claims button below. `sa_connected` gates whether it renders at all - this
  // is deliberately NOT useGetProdaConnectionQuery. As of TAV-3192, `sa_connected` already means
  // "PRODA direct claiming is available for this company in this environment": the API resolves
  // the `feature-proda-direct` flag company-wide (no user identity in the evaluation context) AND
  // the company's PRODA connection, so this single field is the correct source with nothing
  // further to check here.
  //
  // `refetchOnMountOrArgChange` matches the sibling queries on the claim detail page (Claim.tsx):
  // tag invalidation only covers transitions THIS client made, and `sa_connected` moves entirely
  // server-side when the company's PRODA connection changes - so remounting the list must re-ask
  // rather than serve a cached answer about whether the action should be offered at all.
  const { data: claimable } = useGetClaimableClaimsQuery(undefined, { refetchOnMountOrArgChange: true });
  const claimableClaims = claimable?.claims ?? [];
  // TAV-2595: shared by the Submit Claims button below AND the claim-status refresh effect/
  // indicator further down - both gate on `sa_connected`, which as of TAV-3192 folds in the
  // `feature-proda-direct` flag alongside the company's PRODA connection. The flag is resolved
  // company-wide with no user identity in the evaluation context, so every user resolves it
  // identically; there is no per-user divergence to guard against.
  const saConnected = claimable?.sa_connected === true;
  const canAdministerClaims = useCanAdministerClaims();
  // TAV-3101. `saConnected` is a COMPANY fact (is the provider connected at all) and
  // `canAdministerClaims` is a USER one (does this operator hold the elevated claims tag); neither
  // is the per-user `feature-proda-direct` flag. Lodging the provider-wide claim to Services
  // Australia cannot be undone, so both gates must hold before the button, its wrapper and its
  // tooltip mount at all.
  const canSeeSubmitClaims = saConnected && canAdministerClaims;
  const submitClaimsDisabled = claimableClaims.length === 0;

  const claims = data?.rows || [];
  const total = data?.total;

  useEffect(() => {
    if (isActive) {
      setTitle('Claims');
    }
    dispatch(getStatuses());
    dispatch(getServiceTypes());
  }, [isActive]);

  // TAV-2595 (D1). A SEPARATE effect from the one above, which is unconditional and has no guard.
  // Refreshes Services Australia claim + invoice statuses once per tab activation. Resetting the
  // ref on `isActive -> false` lets re-entering the tab fire again while a single activation
  // cannot fire twice.
  const [refreshClaimStatuses, { data: refreshResult }] = useRefreshClaimStatusesMutation();
  const hasSweptRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      hasSweptRef.current = false;
      return;
    }
    // ORDERING HAZARD: `saConnected` resolves asynchronously (it comes from the claimable query,
    // which starts `undefined` until it resolves). Returning here on a falsy value must NOT touch
    // the ref - latching it before the connection state is known would close the activation
    // permanently, and a connected company would never sweep once `claimable` resolves.
    if (!saConnected) return;
    if (hasSweptRef.current) return;
    hasSweptRef.current = true;

    refreshClaimStatuses()
      .unwrap()
      .then(
        (result) => {
          // TAV-2596 AC #7. The sweep also pulls payment statements, and what it could not match is
          // reported in the SAME errors[] array as its own failures - a different shape, with no
          // `message` (see summariseUnmatchedItems). Info, not danger: nothing went wrong, Services
          // Australia paid for items this provider now needs to look at. A sweep with nothing
          // unmatched summarises to null and stays silent, which is the overwhelming majority of
          // them - the toast has to earn a page the user did not ask to be interrupted on.
          //
          // The API stores the same list on the claim as `sa_reconciliation_errors`, because this
          // toast is transient and the mixed case emits the detail exactly once.
          if (!result?.checked) return;
          const unmatched = summariseUnmatchedItems(result.errors);
          if (unmatched) showToast('info', unmatched, UNMATCHED_TOAST_DURATION_MS);
        },
        // The rejection handler is the SECOND argument to `then`, not a trailing `.catch`: a throw
        // inside the success handler above would otherwise land here and be reported to the provider
        // as a Services Australia failure, which it would not be.
        (error) => {
          // A skipped sweep (`checked: false`) is a 200, not an error - only a genuine failure
          // (not_connected/no_claims/fresh reasons are surfaced via `.data`, not a rejection) reaches
          // this handler.
          //
          // `refresh-status` calls Services Australia, so a genuine failure arrives as the COMPLETE
          // vendor string - status code, request path and raw JSON body - which parseSahApiError
          // exists to unwrap (see its TAV-2590 module header). This path needs it more than the
          // button-driven toasts do, not less: the sweep fires from tab activation, so an unparsed
          // blob would be an unreadable red toast the user never asked for, with nothing else on the
          // page explaining it. Safe unconditionally - parseSahApiError falls through to the original
          // string when there is nothing to parse, so the `||` fallback still covers only a genuinely
          // empty message.
          showToast(
            'danger',
            parseSahApiError(getErrorMessage(error)) || 'Could not refresh claim statuses from Services Australia.',
          );
        },
      );
  }, [isActive, saConnected, refreshClaimStatuses, showToast]);

  // Reads `claimable.claims_last_synced_at` first so the indicator is correct on FIRST paint
  // (before any sweep this session has resolved), then the mutation result once it has - both
  // branches of ClaimStatusRefreshResult carry `last_synced_at`.
  const lastSyncedAt = refreshResult?.last_synced_at ?? claimable?.claims_last_synced_at ?? null;

  useEffect(() => {
    const hasValidationsInFlight = claims.some((claim) => claim.is_validating);
    if (!hasValidationsInFlight) return;

    const interval = setInterval(() => {
      refetchClaims();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [claims, refetchClaims]);

  const onClaimClick = (id: string) => {
    navigate(`/support-at-home/claims/${id}`);
  };

  const handleValidateClaim = (id: string) => {
    validateClaim({ id });
  };

  const columns = columnsFactory(onClaimClick, handleValidateClaim);

  return (
    <div className="p-2 claims">
      <Toolbar
        id="claims"
        rightCluster={
          <>
            {/*
              TAV-2595. Company-scoped, so gated on `saConnected` exactly like the refresh effect
              above - hidden (not em-dashed) for a disconnected company, since there is nothing to
              have synced.
            */}
            {saConnected && (
              <span className="me-3 text-muted small" data-cy="claims-last-proda-update">
                Last PRODA Update: {lastSyncedAt ? dayjs(lastSyncedAt).format(dateTimeDisplayFormat) : 'Never'}
              </span>
            )}
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="claims-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            {canSeeSubmitClaims && (
              // The wrapper div is required, not decorative - a native `disabled` button suppresses
              // the hover events TooltipPortal needs to fire, so the tooltip must target this
              // wrapper instead of the button itself (pattern: FormInputGroupRouteMap.tsx:156-171).
              <div id="submit-claims-button-wrapper" className="d-inline-block me-2">
                <Button
                  dataCy="claims-submit-claims-button"
                  id="submit-claims-button"
                  size="sm"
                  // `success`, matching the other Services Australia lodgement actions (Send,
                  // Finalise & Submit). Per the repo convention `info` is for secondary/neutral
                  // actions, and this is the primary action of the page.
                  color="success"
                  disabled={submitClaimsDisabled}
                  onClick={() => setSubmitClaimsOpen(true)}
                >
                  <Icon className="me-2" icon={faFileInvoiceDollar} />
                  Submit Claims
                </Button>
                {submitClaimsDisabled && (
                  <TooltipPortal
                    targetId="submit-claims-button-wrapper"
                    content="No claims ready to claim (Submitted)"
                  />
                )}
              </div>
            )}
            <Button dataCy="claims-new-button" size="sm" color="success" onClick={() => setClaimsWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New
            </Button>
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      <TableView
        id="claims-table"
        columns={columns}
        data={claims}
        isLoading={isLoading}
        emptyMessage="No claims found"
        readonly
        striped
        allowPagination
        totalCount={total}
        onLoadMore={handleLoadMore}
      />
      <ClaimsWizard isOpen={claimsWizardOpen} setIsOpen={setClaimsWizardOpen} />
      <SubmitClaimsModal
        isOpen={submitClaimsOpen}
        setIsOpen={setSubmitClaimsOpen}
        claims={claimableClaims}
        onSubmitted={refetchClaims}
      />
    </div>
  );
};

export default Claims;
